import React, { useEffect, useState } from 'react';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { grantPointsInBulk, getBulkGrantBranchCount } from '../../../api/member-point';
import PointExpireField from '../../point/PointExpireField';
import { getBranchManager } from '../../../api/staff';
import { POINT_TYPE } from '../../../constants/point';
import { errorMessages } from '../../../utils/methods';
import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonSegmentedControl from '../../../form-components/CommonSegmentedControl';
import { FormInputDropdown } from '../../../form-components/FormInputDropdown';
import { FormInputPrice } from '../../../form-components/FormInputPrice';
import { FormInputText } from '../../../form-components/FormInputText';
import MemberGroupMemberEditModal from '../../member/modal/memberGroup/MemberGroupMemberEditModal';
import {
  FieldGroup,
  FieldLabel,
  FieldsBox,
  MemoWrapper,
  PointSuffix,
  PreviewBox,
  PreviewHighlight,
  SegmentWrapper,
} from '../../userDetails/UserPointHistory/UserPointHistoryStyled';
import { TargetRow, TargetOption, TargetRadio, TargetPickButton } from './PointManagementStyled';

const TYPE_OPTIONS = [
  { value: POINT_TYPE.EARN, label: '적립' },
  { value: POINT_TYPE.DEDUCT, label: '차감' },
];

// 회원 상세 포인트 모달과 같은 길이 제한을 쓴다.
const MEMO_MAX_LENGTH = 10;

const TARGET = { ALL: 'ALL', MEMBERS: 'MEMBERS' };

// 차감 방식. FIXED=1인당 같은 금액, ALL=회원마다 보유 잔액 전액(잔액을 0으로).
const DEDUCT_MODE = { FIXED: 'FIXED', ALL: 'ALL' };

const DEFAULT_VALUES = { staffUserId: '', point: '', memo: '' };

/**
 * 포인트 적립/차감 모달 (센터관리 > 포인트 관리).
 *
 * <p>대상은 <b>지점 전체</b>이거나 <b>고른 회원</b>이다. 회원 그룹은 고르기 화면의 '그룹 불러오기'로
 * 좁혀서 담으므로 여기서 따로 다루지 않는다.
 *
 * <p>서버는 전원 성공해야 반영한다. 정액 차감은 한 명이라도 잔액이 모자라면 아무에게도 들어가지
 * 않고 누가 부족한지 메시지로 온다. 그래서 화면에서는 개별 성공/실패를 세지 않는다.
 *
 * <p>차감은 <b>전액 차감</b>도 고를 수 있다. 회원마다 보유 잔액만큼 차감해 0으로 만들고, 잔액이
 * 0인 회원은 서버가 건너뛴다. 금액 입력이 없으므로 총액 미리보기도 없다.
 */
const PointGrantModal = ({ open, onClose, branchId, token }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState(POINT_TYPE.EARN);
  const [deductMode, setDeductMode] = useState(DEDUCT_MODE.FIXED);
  const [target, setTarget] = useState(TARGET.ALL);
  const [pickedMembers, setPickedMembers] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // 지급분 유효기간 { expireDate, noExpire }. 둘 다 비면 지점 규칙. 차감에는 보내지 않는다.
  const [expire, setExpire] = useState({ expireDate: null, noExpire: false });

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues: DEFAULT_VALUES });
  const watchedPoint = watch('point');

  // 닫았다 다시 열면 이전 입력이 남지 않게 한다.
  useEffect(() => {
    if (!open) {
      setType(POINT_TYPE.EARN);
      setDeductMode(DEDUCT_MODE.FIXED);
      setTarget(TARGET.ALL);
      setPickedMembers([]);
      reset(DEFAULT_VALUES);
    }
  }, [open, reset]);

  // 지점 전체 인원. 누르기 전에 몇 명에게 나가는지 보여주려고 서버에서 세어 온다.
  const { data: branchCount } = useQuery({
    queryKey: ['bulkGrantBranchCount', branchId],
    queryFn: () => getBulkGrantBranchCount(branchId, token),
    enabled: open && !!branchId && !!token,
  });

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['branchManager', branchId],
    queryFn: async () => {
      const data = await getBranchManager(branchId, token);
      return (data || []).map(item => ({ value: item.managerId, label: item.managerName }));
    },
    enabled: open && !!branchId && !!token,
  });

  const targetCount =
    target === TARGET.ALL ? branchCount?.memberCount ?? 0 : pickedMembers.length;
  const deductAll = type === POINT_TYPE.DEDUCT && deductMode === DEDUCT_MODE.ALL;

  // 적립으로 돌아가면 차감 방식은 기본값으로 되돌린다.
  const handleTypeChange = next => {
    setType(next);
    if (next !== POINT_TYPE.DEDUCT) setDeductMode(DEDUCT_MODE.FIXED);
  };

  const grantMutation = useMutation({
    mutationFn: payload => grantPointsInBulk(payload, token),
    onSuccess: result => {
      const label = type === POINT_TYPE.EARN ? '적립' : '차감';
      toast.success(
        `${result?.memberCount ?? 0}${t('명에게')} ${(result?.totalPoint ?? 0).toLocaleString()}P ${t(label)}${t('되었습니다')}`,
      );
      queryClient.invalidateQueries({ queryKey: ['branchPointHistories', branchId] });
      queryClient.invalidateQueries({ queryKey: ['memberPoints'] });
      onClose();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const handleSave = handleSubmit(values => {
    if (!values.staffUserId) {
      toast.error(t('담당자를 선택해 주세요'));
      return;
    }
    const pointNum = Number(values.point);
    if (!deductAll && (values.point === '' || Number.isNaN(pointNum) || pointNum <= 0)) {
      toast.error(
        t(type === POINT_TYPE.EARN ? '적립할 포인트를 입력해 주세요' : '차감할 포인트를 입력해 주세요'),
      );
      return;
    }
    if (!targetCount) {
      toast.error(t('대상 회원이 없습니다'));
      return;
    }
    grantMutation.mutate({
      branchId,
      target,
      type,
      point: deductAll ? undefined : pointNum,
      deductAll: deductAll || undefined,
      staffUserId: values.staffUserId,
      memo: values.memo,
      memberIds: target === TARGET.MEMBERS ? pickedMembers.map(m => m.memberId) : undefined,
      expireDate: type === POINT_TYPE.EARN ? expire.expireDate : undefined,
      noExpire: type === POINT_TYPE.EARN && expire.noExpire ? true : undefined,
    });
  });

  const totalPoint = Number(watchedPoint || 0) * targetCount;

  return (
    <>
      <CommonModalLayout
        open={open}
        onClose={onClose}
        title={t('포인트 적립/차감')}
        width={520}
        footer={
          <>
            <CommonModalButton variant="cancel" onClick={onClose}>
              {t('취소')}
            </CommonModalButton>
            <CommonModalButton
              variant="submit"
              onClick={handleSave}
              disabled={grantMutation.isPending}>
              {t('저장')}
            </CommonModalButton>
          </>
        }>
        <SegmentWrapper>
          <CommonSegmentedControl
            options={TYPE_OPTIONS.map(opt => ({ ...opt, label: t(opt.label) }))}
            value={type}
            onChange={handleTypeChange}
          />
        </SegmentWrapper>

        {/* 차감만 방식을 고른다. 전액 차감은 회원마다 잔액이 다르므로 금액을 받지 않는다. */}
        {type === POINT_TYPE.DEDUCT && (
          <TargetRow>
            <TargetOption onClick={() => setDeductMode(DEDUCT_MODE.FIXED)}>
              <TargetRadio $checked={deductMode === DEDUCT_MODE.FIXED} />
              {t('1인당 정액 차감')}
            </TargetOption>
            <TargetOption onClick={() => setDeductMode(DEDUCT_MODE.ALL)}>
              <TargetRadio $checked={deductMode === DEDUCT_MODE.ALL} />
              {t('전액 차감 (잔액을 0으로)')}
            </TargetOption>
          </TargetRow>
        )}

        {/* 대상은 지점 전체이거나 고른 회원이다. 그룹은 고르기 화면의 그룹 불러오기로 좁힌다. */}
        <TargetRow>
          <TargetOption onClick={() => setTarget(TARGET.ALL)}>
            <TargetRadio $checked={target === TARGET.ALL} />
            {t('지점 전체')}
            {branchCount?.memberCount != null && ` (${branchCount.memberCount}${t('명')})`}
          </TargetOption>
          <TargetOption onClick={() => setTarget(TARGET.MEMBERS)}>
            <TargetRadio $checked={target === TARGET.MEMBERS} />
            {t('회원 선택')}
            {pickedMembers.length > 0 && ` (${pickedMembers.length}${t('명')})`}
          </TargetOption>
          <TargetPickButton
            type="button"
            disabled={target !== TARGET.MEMBERS}
            onClick={() => setPickerOpen(true)}>
            {t('회원 고르기')}
          </TargetPickButton>
        </TargetRow>

        <FieldsBox>
          <FieldGroup>
            <FieldLabel>{t('담당자')}</FieldLabel>
            <FormInputDropdown
              control={control}
              name="staffUserId"
              options={staffOptions}
              disabledItem={
                <MenuItem disabled value="">
                  {t('담당자를 선택해 주세요')}
                </MenuItem>
              }
            />
          </FieldGroup>

          {!deductAll && (
            <FieldGroup>
              <FieldLabel>
                {type === POINT_TYPE.EARN ? t('1인당 적립 포인트') : t('1인당 차감 포인트')}
              </FieldLabel>
              <FormInputPrice
                control={control}
                name="point"
                size="small"
                placeholder={t('숫자 입력')}
                InputProps={{ endAdornment: <PointSuffix>P</PointSuffix> }}
              />
            </FieldGroup>
          )}
        </FieldsBox>

        {type === POINT_TYPE.EARN && (
          <PointExpireField branchId={branchId} token={token} onChange={setExpire} resetKey={open} />
        )}

        {/* 정액이면 같은 금액이 인원수만큼 나가므로 총액을, 전액 차감이면 방식만 보여준다. */}
        <PreviewBox>
          {t('대상')} {targetCount}
          {t('명')} ·{' '}
          {deductAll ? (
            <PreviewHighlight>{t('보유 잔액 전액 차감')}</PreviewHighlight>
          ) : (
            <>
              {t('총')}
              <PreviewHighlight>&nbsp;{totalPoint.toLocaleString()}P</PreviewHighlight>
            </>
          )}
        </PreviewBox>

        <MemoWrapper>
          <FieldLabel>{t('메모')}</FieldLabel>
          <FormInputText
            control={control}
            name="memo"
            placeholder={t('10자 이내로 작성해 주세요.')}
            size="small"
            inputProps={{ maxLength: MEMO_MAX_LENGTH }}
          />
        </MemoWrapper>
      </CommonModalLayout>

      {/*
        대상 고르기는 그룹 회원 편집과 같은 화면을 쓴다. onPick 을 주면 서버에 저장하지 않고
        고른 회원만 돌려준다. 문자 수신자 선택과 같은 '그룹 불러오기' 가 고르기 모드에만 붙는다.
      */}
      <MemberGroupMemberEditModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentMembers={pickedMembers}
        title={t('포인트 적립/차감 대상 선택')}
        onPick={setPickedMembers}
      />
    </>
  );
};

export default PointGrantModal;
